/**
 * Specification §3.1, *Missing fields* (DECIDED 20261023): every item in the
 * missing-data notice is a link to its field, the notice carries *Ugrás az
 * első hiányzó mezőhöz* / *Go to the first missing field*, and the continue
 * button goes to the first gap while there is one.
 *
 * `missingItems` is the notice's list with the field each part names. The
 * words are exactly the ones `contactMissing` has always produced — that
 * function is now this list, joined — so the notice reads as it did and only
 * gains its links.
 */

import { emailOk, phoneOk, postcodeOk, vatOk } from './format.ts'
import type { AddressFields, PageState } from './state.ts'
import { shippingAddress } from './state.ts'

export interface MissingPart {
  text: string
  /** The id of the input this part is about. */
  field: string
}

/** One item of the notice: an optional lead-in (*számlázási cím:*) and its parts. */
export interface MissingItem {
  lead: string | null
  parts: MissingPart[]
}

export function missingItems(
  state: PageState,
  t: (hu: string, en: string) => string,
  enquiry: boolean,
): MissingItem[] {
  const items: MissingItem[] = []
  const one = (text: string, field: string): void => {
    items.push({ lead: null, parts: [{ text, field }] })
  }
  if (state.lastName.trim() === '') one(t('vezetéknév', 'last name'), 'cLast')
  if (state.firstName.trim() === '') one(t('keresztnév', 'first name'), 'cFirst')
  if (!emailOk(state.email.trim())) one(t('érvényes e-mail cím', 'a valid email address'), 'cMail')
  if (!phoneOk(state.phone.trim())) {
    one(t('telefonszám (+36… vagy nemzetközi formában)', 'phone number (+36… or international)'), 'cTel')
  }
  if (enquiry) return items

  if (state.business) {
    if (state.company.trim() === '') one(t('cégnév', 'company name'), 'cCo')
    if (!vatOk(state.vatNumber.trim())) {
      one(t('adószám 12345678-2-41 formában', 'tax number as 12345678-2-41'), 'cVat')
    }
  }
  /* Fix list item 1 (sweep, 10 October 2026): name only the parts that are
     actually missing, and never list the delivery address separately while
     it is the billing address. */
  const gaps = (address: AddressFields, prefix: 'b' | 's'): MissingPart[] => [
    ...(postcodeOk(address.postcode.trim()) ? [] : [{ text: t('irányítószám', 'postcode'), field: `${prefix}Zip` }]),
    ...(address.town.trim() !== '' ? [] : [{ text: t('település', 'town'), field: `${prefix}City` }]),
    ...(address.street.trim() !== ''
      ? []
      : [{ text: t('utca és házszám', 'street and number'), field: `${prefix}Street` }]),
  ]
  const billingGaps = gaps(state.billing, 'b')
  if (billingGaps.length > 0) {
    items.push({ lead: t('számlázási cím', 'billing address'), parts: billingGaps })
  }
  if (state.fulfilment === 'delivery' && !state.sameAddress) {
    const deliveryGaps = gaps(shippingAddress(state), 's')
    if (deliveryGaps.length > 0) {
      items.push({ lead: t('szállítási cím', 'delivery address'), parts: deliveryGaps })
    }
  }
  if (state.eventName.trim() === '') one(t('az esemény neve', 'the event name'), 'evname')
  return items
}

/** The notice's items as the sentences they have always been. */
export const missingTexts = (items: MissingItem[]): string[] =>
  items.map((item) => {
    const parts = item.parts.map((part) => part.text).join(', ')
    return item.lead === null ? parts : `${item.lead}: ${parts}`
  })

/**
 * Every field the notice can name, in page order (§3.1: *first* means first
 * on the page). The event name is in the event details, above everything
 * else. The two name fields change places with the language (§5.12), so the
 * order follows what the customer sees.
 */
export function pageOrder(lang: 'hu' | 'en'): string[] {
  return [
    'evname',
    ...(lang === 'hu' ? ['cLast', 'cFirst'] : ['cFirst', 'cLast']),
    'cMail',
    'cTel',
    'cCo',
    'cVat',
    'bZip',
    'bCity',
    'bStreet',
    'sZip',
    'sCity',
    'sStreet',
  ]
}

export function firstMissingField(items: MissingItem[], lang: 'hu' | 'en'): string | null {
  const missing = new Set(items.flatMap((item) => item.parts.map((part) => part.field)))
  return pageOrder(lang).find((field) => missing.has(field)) ?? null
}

/* ------------------------------------------------------------------ *
 * Going to a field
 * ------------------------------------------------------------------ */

/** The same two seconds the offer card's dish highlight lasts. */
export const FIELD_HIGHLIGHT_MS = 2000

const wantsNoMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Marked when a jump flagged it, so the flag can come off once it is valid. */
export const JUMP_FLAG = 'data-jumpflag'

/**
 * Scroll to a field, focus it, mark it invalid and highlight it for two
 * seconds — as the offer card does for a dish, and with the same respect for
 * reduced motion. A closed `<details>` around it is opened first. Returns
 * false where the field is not on the page.
 */
export function jumpToField(id: string, doc: Document = document): boolean {
  const field = doc.getElementById(id)
  if (field === null) return false
  for (let node: HTMLElement | null = field.parentElement; node !== null; node = node.parentElement) {
    if (node instanceof HTMLDetailsElement && !node.open) node.open = true
  }
  field.setAttribute('aria-invalid', 'true')
  field.setAttribute(JUMP_FLAG, '')
  /* Scroll first and focus without a second scroll, so a smooth scroll is not
     cut short by the focus jumping the page. */
  field.scrollIntoView?.({ behavior: wantsNoMotion() ? 'auto' : 'smooth', block: 'center' })
  ;(field as HTMLElement).focus({ preventScroll: true })
  field.classList.add('offerhit')
  const view = doc.defaultView ?? window
  view.setTimeout(() => field.classList.remove('offerhit'), FIELD_HIGHLIGHT_MS)
  return true
}

/**
 * The flag a jump set stays only while the field is still missing: once the
 * customer has filled it, `aria-invalid` comes off again.
 */
export function clearSettledFlags(items: MissingItem[], doc: Document = document): void {
  const missing = new Set(items.flatMap((item) => item.parts.map((part) => part.field)))
  for (const element of doc.querySelectorAll<HTMLElement>(`[${JUMP_FLAG}]`)) {
    if (missing.has(element.id)) continue
    element.removeAttribute('aria-invalid')
    element.removeAttribute(JUMP_FLAG)
  }
}

/**
 * *Tovább a rendeléshez* (specification §3.1): to the first gap while there
 * is one, and only otherwise to the contact step's first name field as
 * before. Answers which of the two it did.
 */
export function continueToOrder(
  goToFirstMissing: () => boolean,
  toContact: () => void,
): 'missing' | 'contact' {
  if (goToFirstMissing()) return 'missing'
  toContact()
  return 'contact'
}
